import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { WhatsappLogo, Copy, Link as LinkIcon, UserPlus, Buildings, Trophy, Sword } from "@phosphor-icons/react";

const APP_URL = window.location.origin;

const inviteTypes = [
  {
    role: "profesor",
    label: "Profesor",
    desc: "Gestion de torneos y estadisticas",
    icon: Trophy,
    color: "#FFB800",
    whatsappMsg: "Te invito a unirte como *Profesor* en Disfruta y Diviertete - nuestra app de padel. Registrate aqui:",
  },
  {
    role: "jugador",
    label: "Jugador",
    desc: "Torneos, retas, canchas y estadisticas",
    icon: Sword,
    color: "#CCFF00",
    whatsappMsg: "Te invito a unirte como *Jugador* en Disfruta y Diviertete - nuestra app de padel. Registrate aqui:",
  },
];

export default function Invitations() {
  const { user } = useAuth();
  const [customMsg, setCustomMsg] = useState("");
  const [customPhone, setCustomPhone] = useState("");

  const getLink = (role) => `${APP_URL}/register?rol=${role}`;

  const copyLink = (role) => {
    navigator.clipboard.writeText(getLink(role));
    toast.success("Link copiado!");
  };

  const shareWhatsApp = (invite, phone) => {
    const msg = encodeURIComponent(`${invite.whatsappMsg}\n${getLink(invite.role)}`);
    const phoneNum = phone ? phone.replace(/\D/g, "") : "";
    const url = phoneNum ? `https://wa.me/${phoneNum}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  const shareCustomWhatsApp = (invite) => {
    const msg = encodeURIComponent(`${customMsg || invite.whatsappMsg}\n${getLink(invite.role)}`);
    const phoneNum = customPhone ? customPhone.replace(/\D/g, "") : "";
    const url = phoneNum ? `https://wa.me/${phoneNum}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  if (!["admin", "directivo"].includes(user?.role)) {
    return (
      <div className="card-tactical p-12 text-center">
        <p className="text-gray-400">No tienes permisos para enviar invitaciones</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="invitations-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Comunidad</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Invitaciones</h1>
        <p className="text-sm text-gray-400 mt-1">Genera links de invitacion y comparte por WhatsApp</p>
      </div>

      <div className="card-tactical p-4 border-blue-500/30 bg-blue-500/5" data-testid="directivo-notice-banner">
        <p className="text-xs text-blue-300">
          <strong className="uppercase tracking-wider">Directivos y administradores</strong> no se registran por invitacion publica. Creacion manual desde <a href="/dashboard/usuarios" className="text-[#CCFF00] hover:underline">Usuarios &rarr; Agregar</a> (solo admin).
        </p>
      </div>

      {/* Quick Send */}
      <div className="card-tactical p-6" data-testid="quick-send">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-4">Envio Rapido por WhatsApp</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="invite-phone-input"
            value={customPhone}
            onChange={(e) => setCustomPhone(e.target.value)}
            placeholder="Numero de WhatsApp (ej: 524641694920)"
            className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] flex-1 placeholder:text-gray-600"
          />
          <Input
            data-testid="invite-msg-input"
            value={customMsg}
            onChange={(e) => setCustomMsg(e.target.value)}
            placeholder="Mensaje personalizado (opcional)"
            className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] flex-1 placeholder:text-gray-600"
          />
        </div>
      </div>

      {/* Invite Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="invite-cards">
        {inviteTypes.map((invite) => (
          <div key={invite.role} className="card-tactical p-6" data-testid={`invite-card-${invite.role}`}>
            <div className="flex items-center gap-3 mb-4">
              <invite.icon size={28} weight="duotone" style={{ color: invite.color }} />
              <div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">{invite.label}</h3>
                <p className="text-xs text-gray-500">{invite.desc}</p>
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-3 mb-4 flex items-center gap-2">
              <LinkIcon size={14} className="text-gray-600 shrink-0" />
              <span className="text-xs text-gray-400 truncate font-['JetBrains_Mono']">{getLink(invite.role)}</span>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => shareWhatsApp(invite, customPhone)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide text-xs"
                data-testid={`whatsapp-${invite.role}`}
              >
                <WhatsappLogo size={16} weight="fill" className="mr-1" /> WhatsApp
              </Button>
              <Button
                onClick={() => copyLink(invite.role)}
                variant="outline"
                className="border-white/20 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 text-xs uppercase font-bold"
                data-testid={`copy-${invite.role}`}
              >
                <Copy size={16} className="mr-1" /> Copiar
              </Button>
            </div>

            {customPhone && (
              <Button
                onClick={() => shareCustomWhatsApp(invite)}
                variant="outline"
                className="w-full mt-2 border-green-600/30 text-green-400 hover:bg-green-600/10 text-xs uppercase font-bold"
                data-testid={`send-direct-${invite.role}`}
              >
                <UserPlus size={14} className="mr-1" /> Enviar a {customPhone}
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Info */}
      <div className="bg-[#141414] border border-white/5 p-5 text-sm text-gray-500" data-testid="invite-info">
        <p className="font-['Barlow_Condensed'] text-xs uppercase tracking-[0.2em] font-semibold text-gray-400 mb-2">Como funciona</p>
        <ul className="space-y-1 text-xs">
          <li>1. El link de invitacion pre-selecciona el rol del nuevo usuario</li>
          <li>2. Al hacer clic, el usuario llega al formulario de registro con el rol ya asignado</li>
          <li>3. Solo necesita llenar su nombre, email/telefono y contrasena</li>
          <li>4. Puedes compartir el link por WhatsApp directamente o copiar para enviar por otro medio</li>
        </ul>
      </div>
    </div>
  );
}
